import type { AiTask } from '@store/reducers/ai/types'
import { END, eventChannel } from 'redux-saga'

import { AI_CHAT_BASE_URL } from '../performRequest'
import { TASK_STREAM_CONNECTION_ERROR_MESSAGE } from './constants'
import { isRecord } from './parseTask'
import type { TaskStreamEvent } from './types'

const buildTaskStatusUrl = (taskId: string, token: string): string => {
  const normalizedBase = AI_CHAT_BASE_URL.endsWith('/')
    ? AI_CHAT_BASE_URL
    : `${AI_CHAT_BASE_URL}/`
  const url = new URL(`status/${taskId}`, normalizedBase)
  // Anonymous (trial, ownerless) projects have no token. The status stream
  // accepts anonymous requests, so only attach the token when we actually have
  // one; otherwise the backend resolves the request to the anonymous user,
  // which matches the anonymously-created task.
  if (token) {
    url.searchParams.set('token', token)
  }
  return url.toString()
}

export const createTaskStatusChannel = (task: AiTask) =>
  eventChannel<TaskStreamEvent>((emit) => {
    const token =
      task.metadata?.authToken || localStorage.getItem('token') || ''

    const eventSource = new EventSource(buildTaskStatusUrl(task.id, token))
    let closed = false

    const close = () => {
      if (closed) {
        return
      }

      closed = true
      eventSource.close()
    }

    const onCancelGeneration = () => {
      emit({ type: 'cancelled' })
      emit(END)
    }

    eventSource.onmessage = (event: MessageEvent<string>) => {
      let payload: unknown

      try {
        payload = JSON.parse(event.data)
      } catch {
        return
      }

      if (!isRecord(payload) || typeof payload.type !== 'string') {
        return
      }

      if (payload.type === 'message' && isRecord(payload.message)) {
        const role =
          typeof payload.message.role === 'string' ? payload.message.role : null
        const content =
          typeof payload.message.content === 'string'
            ? payload.message.content
            : null

        emit({
          type: 'message',
          message: {
            role,
            content,
          },
        })
        return
      }

      if (payload.type === 'task_updated' && isRecord(payload.task)) {
        const streamTask = payload.task as Record<string, unknown>
        if (
          typeof streamTask.id === 'string' &&
          typeof streamTask.status === 'string'
        ) {
          emit({
            type: 'task_updated',
            task: streamTask as unknown as AiTask,
          })
        }
      }
    }

    eventSource.onerror = () => {
      if (closed) {
        return
      }

      emit({
        type: 'stream_error',
        error: new Error(TASK_STREAM_CONNECTION_ERROR_MESSAGE),
      })
      emit(END)
    }

    window.addEventListener('CancelAiGeneration', onCancelGeneration)

    return () => {
      window.removeEventListener('CancelAiGeneration', onCancelGeneration)
      close()
    }
  })
